import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';

import { Logo } from '../shared/ui/Logo';
import { logout } from '../features/auth/api';
import { useSession } from '../features/auth/session';

const roleLabel: Record<string, string> = {
  admin: 'Администратор',
  doctor: 'Врач',
  user: 'Пользователь',
};

const linkBase = 'rounded-lg px-3 py-2 text-sm font-medium transition-colors';
const navCls = ({ isActive }: { isActive: boolean }) =>
  `${linkBase} ${isActive ? 'bg-primary-050 text-primary' : 'text-muted hover:bg-canvas hover:text-ink'}`;

export function AppLayout() {
  const { user } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'admin';
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const onLogout = async () => {
    await logout();
    queryClient.clear();
    navigate('/login', { replace: true });
  };

  const items = (
    <>
      <NavLink to="/patients" end className={navCls} onClick={() => setMenuOpen(false)}>
        Пациенты
      </NavLink>
      <NavLink to="/software" className={navCls} onClick={() => setMenuOpen(false)}>
        Программы
      </NavLink>
      {isAdmin && (
        <>
          <NavLink to="/admin/doctors" className={navCls} onClick={() => setMenuOpen(false)}>
            Врачи
          </NavLink>
          <NavLink to="/admin/clinics" className={navCls} onClick={() => setMenuOpen(false)}>
            Клиники
          </NavLink>
          <NavLink to="/admin/dashboard" className={navCls} onClick={() => setMenuOpen(false)}>
            Дашборд
          </NavLink>
          <NavLink to="/admin/partnership" className={navCls} onClick={() => setMenuOpen(false)}>
            Заявки
          </NavLink>
        </>
      )}
    </>
  );

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 border-b border-line bg-surface/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-6">
            <Link to="/" onClick={() => setMenuOpen(false)}>
              <Logo size="sm" />
            </Link>
            <nav className="hidden items-center gap-1 md:flex">{items}</nav>
          </div>

          <div className="hidden items-center gap-3 md:flex">
            {user && (
              <NavLink
                to="/profile"
                className="flex items-center gap-2 rounded-lg px-2 py-1 text-sm text-muted hover:bg-canvas hover:text-ink"
              >
                <span className="max-w-[14rem] truncate">{user.fullName || user.email}</span>
                <span className="rounded-full bg-canvas px-2 py-0.5 text-xs font-medium text-muted">
                  {roleLabel[user.role] ?? user.role}
                </span>
              </NavLink>
            )}
            <button
              type="button"
              onClick={onLogout}
              className="rounded-lg border border-line px-3 py-1.5 text-sm text-muted hover:bg-canvas hover:text-ink"
            >
              Выход
            </button>
          </div>

          <button
            type="button"
            aria-label="Меню"
            onClick={() => setMenuOpen((v) => !v)}
            className="rounded-lg border border-line p-2 text-ink md:hidden"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {menuOpen ? (
                <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
              ) : (
                <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
              )}
            </svg>
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-line bg-surface px-4 py-3 md:hidden">
            <nav className="flex flex-col gap-1">{items}</nav>
            <div className="mt-3 flex items-center justify-between border-t border-line pt-3">
              {user && (
                <NavLink
                  to="/profile"
                  onClick={() => setMenuOpen(false)}
                  className="text-sm text-muted hover:text-ink"
                >
                  {user.fullName || user.email}
                  <span className="ml-2 rounded-full bg-canvas px-2 py-0.5 text-xs">
                    {roleLabel[user.role] ?? user.role}
                  </span>
                </NavLink>
              )}
              <button
                type="button"
                onClick={onLogout}
                className="rounded-lg border border-line px-3 py-1.5 text-sm text-muted"
              >
                Выход
              </button>
            </div>
          </div>
        )}
      </header>

      <main className="mx-auto w-full max-w-6xl px-4 py-6 sm:py-8">
        <Outlet />
      </main>
    </div>
  );
}
